import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  PEAK_INHALATION_CAUTION,
  PEAK_INHALATION_SEGMENTS,
  POST_HOLD_MONITOR_CUE,
  PURGE_BREATHING,
  VENTILATION_BREATHING,
} from '../../data/apneaProtocols';
import { ABORT_TRIGGERS } from '../../data/blackoutSigns';
import { formatSeconds } from '../../utils/apneaTables';
import { ApneaCue, ApneaAudio, cueSpeech } from '../../utils/apneaAudio';
import {
  ApneaPhaseKind,
  ApneaTimerSet,
  ApneaTimerSnapshot,
  RecordedSet,
  clearSnapshot,
  readSnapshot,
  useApneaTimer,
} from '../../hooks/useApneaTimer';
import { useWakeLock } from '../../hooks/useWakeLock';
import BreathCounter from './BreathCounter';
import BreathingGuide from './BreathingGuide';
import HoldCounter from './HoldCounter';
import { LEARN_ANCHORS } from './LearnReference';
import PhaseCounter from './PhaseCounter';

/**
 * The session runner.
 *
 * Designed for eyes-closed use: audio cues carry the transitions, the layout
 * keeps one large numeral per phase, and abort is always one tap. The phase
 * machine (useApneaTimer) owns all timing; this component only renders it and
 * fires cues on transitions.
 */

const PHASE_LABEL: Record<ApneaPhaseKind, string> = {
  idle: 'Ready',
  breatheUp: 'Breathe up',
  purge: 'Purge breathing',
  peakInhalation: 'Peak inhalation',
  hold: 'Hold',
  recovery: 'Recovery breaths',
  monitor: 'Monitor',
  rest: 'Rest',
  complete: 'Complete',
};

/**
 * Body-scan sequence, from the sources' relaxation protocol. Cycled slowly during a
 * hold: every bit of tension found and released is oxygen saved, and giving the mind
 * a task is itself the technique rather than a distraction from it.
 */
export const BODY_SCAN = [
  'Soften your forehead and eyes',
  'Unclench your jaw',
  'Drop your shoulders',
  'Let your hands go loose',
  'Release your abdomen',
  'Soften your legs and feet',
];

/** Seconds each body-scan prompt stays on screen. */
const SCAN_INTERVAL_SECONDS = 12;

/** How far ahead of a prescribed hold target the warning cue fires. */
const HOLD_WARNING_LEAD_SECONDS = 30;

/** Checkpoint cadence for open-ended holds: every 30s to 2 minutes, then every 60s. */
const HOLD_CHECKPOINT_SWITCH_SECONDS = 120;
const HOLD_CHECKPOINT_EARLY_INTERVAL = 30;
const HOLD_CHECKPOINT_LATE_INTERVAL = 60;

/** The next checkpoint after `lastCued`, given the two-speed cadence above. */
const nextHoldCheckpoint = (lastCued: number): number =>
  lastCued < HOLD_CHECKPOINT_SWITCH_SECONDS
    ? lastCued + HOLD_CHECKPOINT_EARLY_INTERVAL
    : lastCued + HOLD_CHECKPOINT_LATE_INTERVAL;

/**
 * What to say during a hold. Before the first contraction the work is relaxation;
 * once contractions start, the message shifts to relaxing *into* them, because the
 * sources are explicit that contractions are a CO2 signal rather than a warning and
 * that fighting them wastes oxygen.
 */
export const holdCoachingCue = (elapsedSeconds: number, contractionMarked: boolean): string => {
  if (contractionMarked) {
    return 'Contractions are a CO₂ signal, not a warning. Relax into them — don\'t fight.';
  }
  return BODY_SCAN[Math.floor(elapsedSeconds / SCAN_INTERVAL_SECONDS) % BODY_SCAN.length];
};

const PHASE_CUE: Partial<Record<ApneaPhaseKind, ApneaCue>> = {
  hold: 'holdStart',
  recovery: 'holdEnd',
  monitor: 'monitorStart',
  rest: 'setComplete',
  complete: 'sessionComplete',
};

interface SessionRunnerProps {
  sets: ApneaTimerSet[];
  breatheUpSeconds: number;
  purgeBreaths?: number;
  includePeakInhalation?: boolean;
  audioEnabled?: boolean;
  speechEnabled?: boolean;
  vibrationEnabled?: boolean;
  /** Enables crash recovery. Omit to run without persistence. */
  persistKey?: string;
  onFinished: (records: RecordedSet[], aborted: boolean, abortReason?: string) => void;
  onCancel: () => void;
}

const SessionRunner: React.FC<SessionRunnerProps> = ({
  sets,
  breatheUpSeconds,
  purgeBreaths = 0,
  includePeakInhalation = true,
  audioEnabled = true,
  speechEnabled = false,
  vibrationEnabled = true,
  persistKey,
  onFinished,
  onCancel,
}) => {
  const [showAbortSigns, setShowAbortSigns] = useState(false);
  const [confirmAbort, setConfirmAbort] = useState(false);
  const [peakSegment, setPeakSegment] = useState(0);
  const finishedRef = useRef(false);
  // Read once on mount: an unfinished session from a previous page load.
  const [recoverable, setRecoverable] = useState<ApneaTimerSnapshot | null>(
    () => (persistKey ? readSnapshot(persistKey) : null)
  );

  const audio = useMemo(
    () => new ApneaAudio({
      tones: audioEnabled,
      speech: speechEnabled,
      vibration: vibrationEnabled,
    }),
    [audioEnabled, speechEnabled, vibrationEnabled]
  );

  const wakeLock = useWakeLock();
  // The breathing guide animates a scaling circle; honour the OS motion preference.
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const handlePhaseChange = useCallback((phase: ApneaPhaseKind, setNumber: number) => {
    setPeakSegment(0);
    const cue = PHASE_CUE[phase];
    if (!cue) return;
    audio.play(cue);
    const speech = cueSpeech(cue, { setNumber, totalSets: sets.length });
    if (speech) audio.speak(speech);
  }, [audio, sets.length]);

  const { state, controls } = useApneaTimer({
    sets,
    breatheUpSeconds,
    purgeBreaths,
    includePeakInhalation,
    persistKey,
    onPhaseChange: handlePhaseChange,
  });

  // Cue the moment a prescribed hold reaches target, so the user knows without looking.
  const targetCued = useRef(false);
  useEffect(() => {
    if (state.phase !== 'hold') {
      targetCued.current = false;
      return;
    }
    if (state.holdTarget !== null && state.phaseElapsed >= state.holdTarget && !targetCued.current) {
      targetCued.current = true;
      audio.play('holdTarget');
    }
  }, [state.phase, state.phaseElapsed, state.holdTarget, audio]);

  // 30 seconds before a prescribed target, so the diver can anticipate the end
  // of the hold without watching the clock.
  const warningCued = useRef(false);
  useEffect(() => {
    if (state.phase !== 'hold') {
      warningCued.current = false;
      return;
    }
    const target = state.holdTarget;
    if (
      target !== null
      && target > HOLD_WARNING_LEAD_SECONDS
      && state.phaseElapsed >= target - HOLD_WARNING_LEAD_SECONDS
      && !warningCued.current
    ) {
      warningCued.current = true;
      audio.play('holdWarning');
      const speech = cueSpeech('holdWarning', { holdSeconds: HOLD_WARNING_LEAD_SECONDS });
      if (speech) audio.speak(speech);
    }
  }, [state.phase, state.phaseElapsed, state.holdTarget, audio]);

  // Open-ended holds (no prescribed target — e.g. a max-effort or BOLT-style hold)
  // have no target cue to anticipate, so call out elapsed time at intervals instead:
  // every 30s for the first two minutes, then every 60s beyond that.
  const lastCheckpointCued = useRef(0);
  useEffect(() => {
    if (state.phase !== 'hold') {
      lastCheckpointCued.current = 0;
      return;
    }
    if (state.holdTarget !== null) return;
    const checkpoint = nextHoldCheckpoint(lastCheckpointCued.current);
    if (state.phaseElapsed >= checkpoint) {
      lastCheckpointCued.current = checkpoint;
      audio.play('holdCheckpoint');
      const speech = cueSpeech('holdCheckpoint', { holdSeconds: checkpoint });
      if (speech) audio.speak(speech);
    }
  }, [state.phase, state.phaseElapsed, state.holdTarget, audio]);

  useEffect(() => () => audio.dispose(), [audio]);

  // Report completion exactly once, whether the session ended naturally or was aborted.
  useEffect(() => {
    if (state.phase === 'complete' && !finishedRef.current) {
      finishedRef.current = true;
      void wakeLock.release();
      onFinished(state.records, false);
    }
  }, [state.phase, state.records, onFinished, wakeLock]);

  const handleStart = async () => {
    // Priming must happen inside the gesture, or the first cues are silent.
    await audio.prime();
    await wakeLock.request();
    audio.play('sessionStart');
    controls.start();
  };

  const handleAbort = () => {
    setConfirmAbort(false);
    finishedRef.current = true;
    void wakeLock.release();
    controls.abort('user ended session');
    onFinished(state.records, true, 'Ended early');
  };

  const setContext = state.setNumber > 0
    ? `set ${state.setNumber} of ${state.totalSets}`
    : `${state.totalSets} sets`;

  const renderPhase = () => {
    switch (state.phase) {
      case 'idle':
        if (recoverable) {
          const completed = recoverable.records.length;
          return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" gutterBottom>Unfinished session found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {PHASE_LABEL[recoverable.phase]} · set {recoverable.setIndex + 1} of{' '}
                {recoverable.setCount} · {completed} set{completed === 1 ? '' : 's'} recorded
              </Typography>
              <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
                Resuming continues from the real elapsed time, so any gap since you left
                counts as it actually happened. If that gap was long, discarding and
                logging what you completed is the honest option.
              </Alert>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={async () => {
                    await audio.prime();
                    await wakeLock.request();
                    if (!controls.restore(recoverable)) {
                      // Shape mismatch: the stored session was a different table.
                      if (persistKey) clearSnapshot(persistKey);
                    }
                    setRecoverable(null);
                  }}
                >
                  Resume
                </Button>
                <Button
                  onClick={() => {
                    // Keep what was completed rather than throwing the session away.
                    const records = recoverable.records;
                    if (persistKey) clearSnapshot(persistKey);
                    setRecoverable(null);
                    if (records.length > 0) {
                      finishedRef.current = true;
                      onFinished(records, true, 'Recovered after reload');
                    }
                  }}
                >
                  Discard and log
                </Button>
              </Stack>
            </Box>
          );
        }
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>Ready when you are</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {state.totalSets} sets · about {formatSeconds(
                sets.reduce((sum, s) => sum + (s.holdSeconds ?? 0) + s.ventSeconds, breatheUpSeconds)
              )} total
            </Typography>
            <Button variant="contained" size="large" onClick={handleStart} sx={{ minWidth: 200 }}>
              Start session
            </Button>
            {!wakeLock.supported && (
              <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                This browser cannot keep the screen awake. Increase your device screen
                timeout so the display does not sleep mid-session.
              </Alert>
            )}
          </Box>
        );

      case 'breatheUp':
      case 'rest':
        return (
          <>
            <PhaseCounter
              label={PHASE_LABEL[state.phase]}
              remainingSeconds={state.phaseRemaining ?? 0}
              totalSeconds={state.phase === 'breatheUp'
                ? breatheUpSeconds
                : sets[state.setNumber - 1]?.ventSeconds ?? 0}
              cue="Ventilation breathing — quick in, long out"
              nextLabel={state.nextPhase ? PHASE_LABEL[state.nextPhase] : undefined}
            />
            {/* The cadence is the point of this phase, so show it per breath rather
                than leaving the diver to guess at "breathe slowly" for two minutes. */}
            <BreathingGuide
              steps={VENTILATION_BREATHING.steps}
              elapsedSeconds={state.phaseElapsed}
              reducedMotion={prefersReducedMotion}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            >
              Inhale to a comfortable volume without lifting the shoulders. Keep the
              inhale quick — a slow one raises your heart rate.
            </Typography>
          </>
        );

      case 'monitor':
        return (
          <PhaseCounter
            label="Monitor"
            remainingSeconds={state.phaseRemaining ?? 0}
            totalSeconds={30}
            cue={POST_HOLD_MONITOR_CUE}
            nextLabel={state.nextPhase ? PHASE_LABEL[state.nextPhase] : undefined}
            footnote={
              <Link
                href={`?tab=learn#${LEARN_ANCHORS.recoveryBreathing}`}
                target="_blank"
                rel="noopener"
                variant="caption"
              >
                Why this matters (opens in a new tab)
              </Link>
            }
          />
        );

      case 'purge':
        return (
          <BreathCounter
            label="Purge breathing"
            count={state.breathCount}
            target={state.breathTarget}
            cue="1 second in, 4 seconds out"
            capWarning={PURGE_BREATHING.caution}
            onBreath={controls.advance}
          />
        );

      case 'recovery':
        return (
          <BreathCounter
            label="Recovery breaths"
            count={state.breathCount}
            target={state.breathTarget}
            cue="Sharp in, sharp out — 1 second each"
            // The WHY belongs here rather than in a help page: these are the breaths
            // that prevent blackout, and mid-session is when that matters.
            note={
              'The most important breaths you will take. They swap CO₂-laden air for '
              + 'oxygen-rich air — do all six before you rest, every time.'
            }
            onBreath={() => {
              audio.play('recoveryBreath');
              controls.advance();
            }}
          />
        );

      case 'peakInhalation':
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="overline" color="text.secondary">Peak inhalation</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
              {PEAK_INHALATION_SEGMENTS[peakSegment].segment}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {PEAK_INHALATION_SEGMENTS[peakSegment].cue}
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{ mt: 3, minWidth: 200 }}
              onClick={() => {
                if (peakSegment + 1 < PEAK_INHALATION_SEGMENTS.length) {
                  setPeakSegment(peakSegment + 1);
                } else {
                  controls.advance();
                }
              }}
            >
              {peakSegment + 1 < PEAK_INHALATION_SEGMENTS.length ? 'Next' : 'Begin hold'}
            </Button>
            <Alert severity="warning" sx={{ mt: 3, textAlign: 'left' }}>
              {PEAK_INHALATION_CAUTION}
            </Alert>
            <Link
              href={`?tab=learn#${LEARN_ANCHORS.peakInhalation}`}
              target="_blank"
              rel="noopener"
              variant="caption"
              sx={{ display: 'inline-block', mt: 1.5 }}
            >
              Why this sequence? (opens in a new tab)
            </Link>
          </Box>
        );

      case 'hold':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <HoldCounter
              elapsedSeconds={state.phaseElapsed}
              targetSeconds={state.holdTarget}
              firstContractionAt={state.firstContractionAt}
            />

            {/* Relaxation is the skill being trained, and every bit of tension
                released is oxygen saved — so the hold gets active coaching rather
                than just a number. The prompt rotates so it stays readable at a
                glance instead of becoming wallpaper. */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {holdCoachingCue(state.phaseElapsed, state.firstContractionAt !== null)}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
              <Tooltip title="Tap the moment you first feel a diaphragm twitch or flutter — a normal CO₂ signal, not a reason to stop. It's logged for your training trend only.">
                <span>
                  <Button
                    variant="outlined"
                    onClick={controls.markContraction}
                    disabled={state.firstContractionAt !== null}
                  >
                    {state.firstContractionAt !== null ? 'Contraction marked' : 'Mark contraction'}
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" onClick={controls.endHold}>
                End hold
              </Button>
            </Stack>
          </Box>
        );

      case 'complete':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6">Session complete</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {state.records.length} set{state.records.length === 1 ? '' : 's'} recorded
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Always-visible context: phase, set, and total elapsed. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={PHASE_LABEL[state.phase]} color="primary" size="small" />
          <Typography variant="caption" color="text.secondary">{setContext}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatSeconds(state.sessionElapsed)} elapsed
        </Typography>
      </Stack>

      <Divider sx={{ mb: 1 }} />

      {renderPhase()}

      {state.isRunning && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              color="warning"
              startIcon={<Warning />}
              onClick={() => setShowAbortSigns(true)}
            >
              Warning signs
            </Button>
            {state.phase !== 'hold' && (
              <Button size="small" onClick={state.isPaused ? controls.resume : controls.pause}>
                {state.isPaused ? 'Resume' : 'Pause'}
              </Button>
            )}
            {state.setNumber < state.totalSets && (
              <Tooltip title="Skips straight to rest before the next set — including recovery breathing for this one. The skipped set is not recorded, but the rest of the session continues normally.">
                <Button size="small" onClick={controls.skipSet}>Skip set</Button>
              </Tooltip>
            )}
            <Button size="small" color="error" onClick={() => setConfirmAbort(true)}>
              End session
            </Button>
          </Stack>
        </>
      )}

      {!state.isRunning && state.phase === 'idle' && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button size="small" onClick={onCancel}>Back</Button>
        </Box>
      )}

      {/* Abort signs — reachable in one tap during a hold, never buried. */}
      <Dialog open={showAbortSigns} onClose={() => setShowAbortSigns(false)} fullWidth maxWidth="sm">
        <DialogTitle>Stop the session if you notice any of these</DialogTitle>
        <DialogContent dividers>
          {ABORT_TRIGGERS.map(group => (
            <Box key={group.category} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">{group.category}</Typography>
              <List dense disablePadding>
                {group.triggers.map(t => (
                  <ListItem key={t} disableGutters sx={{ py: 0.25 }}>
                    <ListItemText primary={t} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
          <Alert severity="error">
            These are reasons to stop, not challenges to push through. After any loss of
            motor control or blackout, do no further breath-hold training today.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAbortSigns(false)}>Close</Button>
          <Button color="error" onClick={handleAbort}>End session now</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAbort} onClose={() => setConfirmAbort(false)}>
        <DialogTitle>End this session?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Completed sets are kept. Ending early is a normal outcome, not a failure.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAbort(false)}>Keep going</Button>
          <Button color="error" onClick={handleAbort}>End session</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SessionRunner;
